import React from "react";
import { Easing, interpolate, useCurrentFrame } from "remotion";
import { C } from "./brand";
import { BOOKED_PILL } from "./screens.generated";

// ---------- layout: one choreography, two frames ----------
export type Lay = {
	W: number;
	H: number;
	land: boolean;
	k: number; // artboard px -> canvas px for the cards
	sx: number; // stage centre x
	top: number; // top of the card area
	routeY: number;
	gap: number; // chip spacing in the route row
	chip: number; // chip scale in the row
	text: { x: number; top: number; w: number; align: "left" | "center"; size: number; sub: number; eyebrow: number };
	hero: { pts: [number, number][]; s: number };
};

export const LAND: Lay = {
	W: 1920,
	H: 1080,
	land: true,
	k: 1.6,
	sx: 1340,
	top: 222,
	routeY: 128,
	gap: 330,
	chip: 1.0,
	text: { x: 150, top: 380, w: 830, align: "left", size: 66, sub: 28, eyebrow: 20 },
	hero: {
		pts: [
			[1030, 700],
			[1330, 390],
			[1640, 640],
		],
		s: 1.3,
	},
};

export const PORT: Lay = {
	W: 1080,
	H: 1920,
	land: false,
	k: 2.1,
	sx: 540,
	top: 730,
	routeY: 626,
	gap: 318,
	chip: 1.0,
	text: { x: 80, top: 170, w: 920, align: "center", size: 74, sub: 34, eyebrow: 24 },
	hero: {
		pts: [
			[270, 1450],
			[560, 1070],
			[820, 1370],
		],
		s: 1.5,
	},
};

// ---------- motion helpers ----------
export const cl = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
export const eo = Easing.bezier(0.16, 1, 0.3, 1);
export const eio = Easing.bezier(0.65, 0, 0.35, 1);
export const ramp = (f: number, a: number, b: number, e = eo) => interpolate(f, [a, b], [0, 1], { ...cl, easing: e });
export const mix = (t: number, a: number, b: number) => a + (b - a) * t;

// place a box of (w,h) artboard px centred on (cx,cy) at scale s
export const Place: React.FC<{
	cx: number;
	cy: number;
	w: number;
	h: number;
	s: number;
	rot?: number;
	o?: number;
	z?: number;
	children: React.ReactNode;
}> = ({ cx, cy, w, h, s, rot = 0, o = 1, z = 1, children }) =>
	o <= 0.001 ? null : (
		<div
			style={{
				position: "absolute",
				left: cx - w / 2,
				top: cy - h / 2,
				width: w,
				height: h,
				transform: `scale(${s}) rotate(${rot}deg)`,
				opacity: o,
				zIndex: z,
			}}
		>
			{children}
		</div>
	);

export const BookedPill: React.FC = () => <div data-scr="pill" dangerouslySetInnerHTML={{ __html: BOOKED_PILL }} />;

// resample a polyline to even spacing, return points and cumulative position (0..1)
export const evenDots = (poly: [number, number][], spacing: number, maxDots = 3000) => {
	// a projected route can run far off-screen (or behind the camera): never allocate without bound
	poly = poly.filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y) && Math.abs(x) < 1e6 && Math.abs(y) < 1e6);
	if (poly.length < 2) return [];
	const segs: number[] = [0];
	for (let i = 1; i < poly.length; i++) segs.push(segs[i - 1] + Math.hypot(poly[i][0] - poly[i - 1][0], poly[i][1] - poly[i - 1][1]));
	const total = segs[segs.length - 1] || 1;
	const dots: { x: number; y: number; p: number }[] = [];
	let j = 0;
	const step = Math.max(spacing, total / maxDots);
	for (let d = 0; d <= total; d += step) {
		while (j < segs.length - 2 && segs[j + 1] < d) j++;
		const t = (d - segs[j]) / (segs[j + 1] - segs[j] || 1);
		dots.push({ x: mix(t, poly[j][0], poly[j + 1][0]), y: mix(t, poly[j][1], poly[j + 1][1]), p: d / total });
	}
	return dots;
};

// finger tap on the canvas
export const TapDot: React.FC<{ x: number; y: number; at: number; size?: number }> = ({ x, y, at, size = 64 }) => {
	const f = useCurrentFrame() - at;
	if (f < -12 || f > 22) return null;
	const inO = interpolate(f, [-12, -4], [0, 1], cl);
	const outO = interpolate(f, [8, 18], [1, 0], cl);
	const press = f < 0 ? 1 : interpolate(f, [0, 3, 8], [1, 0.72, 1], cl);
	const dx = interpolate(f, [-12, -2], [size * 0.8, 0], { ...cl, easing: eo });
	const dy = interpolate(f, [-12, -2], [size * 1.2, 0], { ...cl, easing: eo });
	const ring = interpolate(f, [0, 16], [0.5, 2.3], cl);
	const ringO = interpolate(f, [0, 16], [0.7, 0], cl);
	return (
		<>
			<div
				style={{
					position: "absolute",
					left: x - size * 0.62,
					top: y - size * 0.62,
					width: size * 1.24,
					height: size * 1.24,
					borderRadius: 999,
					border: `4px solid ${C.green}`,
					transform: `scale(${ring})`,
					opacity: f >= 0 ? ringO : 0,
					zIndex: 90,
				}}
			/>
			<div
				style={{
					position: "absolute",
					left: x - size / 2 + dx,
					top: y - size / 2 + dy,
					width: size,
					height: size,
					borderRadius: 999,
					background: "rgba(15,62,74,.26)",
					border: "4px solid rgba(255,255,255,.95)",
					boxShadow: "0 8px 22px rgba(15,62,74,.25)",
					transform: `scale(${press})`,
					opacity: inO * outO,
					zIndex: 91,
				}}
			/>
		</>
	);
};
